import { readEml } from 'eml-parse-js';
import MsgReader from '@kenjiuno/msgreader';
import { decompressRTF } from '@kenjiuno/decompressrtf';
import { deEncapsulateSync } from 'rtf-stream-parser';
import * as iconvLite from 'iconv-lite';
import { useStore } from '../../context';
import { useState } from 'react';

interface ParsedEmlAttachment {
  id?: string;
  contentId?: string;
  contentType: string;
  data: Uint8Array;
}

interface ParsedEmlContent {
  text: string;
  html: string;
  attachments: ParsedEmlAttachment[];
}

// Convert plain text to HTML
const textToHtml = (text: string): string => {
  // Escape HTML special characters
  const escapeHtml = (str: string) => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const escapedText = escapeHtml(text);
  
  // Replace newlines with <br> tags and wrap in a pre-styled div to preserve formatting
  const htmlContent = escapedText.replace(/\n/g, '<br>');
  
  return `
    <div style="font-family: monospace; white-space: pre-wrap; word-wrap: break-word; max-width: 100%; overflow-x: auto;">
      ${htmlContent}
    </div>
  `;
};

const sanitizeHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Remove script elements
  const scripts = doc.querySelectorAll('script');
  scripts.forEach((script) => script.remove());

  // Remove potentially harmful attributes
  const elements = doc.body.querySelectorAll('*');
  elements.forEach((element) => {
    const attributes = Array.from(element.attributes);
    for (const attr of attributes) {
      if (
        attr.name.startsWith('on') ||
        (['src', 'href'].includes(attr.name) && !attr.value.startsWith('http'))
      ) {
        element.removeAttribute(attr.name);
      }
    }
  });

  return doc.body.innerHTML;
};

// Convert Uint8Array to Base64
const arrayBufferToBase64 = (buffer: Uint8Array) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

const extractImages = (emlContent: ParsedEmlContent) => {
  const images: { cid: string; data: string; mimeType: string }[] = [];

  if (emlContent.attachments) {
    emlContent.attachments.forEach((attachment: ParsedEmlAttachment) => {
      const cid = attachment.contentId || attachment.id; // Check both fields
      if (attachment.contentType.startsWith('image/') && cid) {
        images.push({
          cid,
          data: arrayBufferToBase64(attachment.data),
          mimeType: attachment.contentType,
        });
      }
    });
  }

  return images;
};

const serveImage = (image: { cid: string; data: string; mimeType: string }) => {
  const base64Image = `data:${image.mimeType};base64,${image.data}`;
  return base64Image;
};

const replaceCidUrls = (
  htmlContent: string,
  images: { cid: string; data: string; mimeType: string }[]
) => {
  let updatedHtml = htmlContent;

  images.forEach((image) => {
    const cidUrl = `cid:${image.cid}`;
    const imageUrl = serveImage(image);
    const regex = new RegExp(`src=["']cid:${image.cid}["']`, 'g');
    updatedHtml = updatedHtml.replace(regex, `src="${imageUrl}"`);
  });

  return updatedHtml;
};

export const useEmail = () => {
  const file = useStore(({ file: f }) => f.getFile());
  const [emailContent, setEmailContent] = useState<{
    text: string;
    html: string;
  }>({ text: '', html: '' });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchParsedEml = async (file: File): Promise<ParsedEmlContent> => {
    const fileText = await file.text();
    return new Promise((resolve, reject) => {
      readEml(fileText, (error: any, data: any) => {
        if (error) {
          reject(error);
        } else {
          const parsedContent: ParsedEmlContent = {
            text: data.text || '',
            html: data.html || '',
            attachments: data.attachments || [],
          };
          resolve(parsedContent);
        }
      });
    });
  };

  const fetchParsedMsg = async (file: File): Promise<ParsedEmlContent | null> => {
    // Read the MSG file as an ArrayBuffer
    const msgBuffer = await file.arrayBuffer();

    const msgReader = new MsgReader(msgBuffer);
    const msgData = msgReader.getFileData();

    // Helper function to convert various types to string
    const toText = (value: string | Buffer | Uint8Array | undefined): string => {
      if (!value) return '';
      if (typeof value === 'string') {
        return value;
      }
      if (Buffer.isBuffer(value)) {
        return value.toString('utf8');
      }
      if (value instanceof Uint8Array) {
        const slice = value.buffer.slice(
          value.byteOffset,
          value.byteOffset + value.byteLength
        );
        return Buffer.from(slice).toString('utf8');
      }
      return '';
    };

    // Extract attachments
    const attachments: ParsedEmlAttachment[] = [];
    if (msgData.attachments && Array.isArray(msgData.attachments)) {
      msgData.attachments.forEach((attachment: any) => {
        if (attachment.data) {
          attachments.push({
            id: attachment.fileName || attachment.name,
            contentId: attachment.contentId,
            contentType: attachment.mimeType || attachment.contentType || 'application/octet-stream',
            data: attachment.data instanceof Uint8Array
              ? attachment.data
              : new Uint8Array(attachment.data),
          });
        }
      });
    }

    let html = '';
    let text = '';

    // Try to extract and decompress RTF, then convert to HTML
    const compressedRtf = msgData.compressedRtf;
    if (compressedRtf) {
      // Decompress the RTF
      const decompressedRtfArray = decompressRTF(Array.from(compressedRtf));
      const decompressedRtf = Buffer.from(decompressedRtfArray).toString('ascii');

      // Convert RTF to HTML using rtf-stream-parser
      const result = deEncapsulateSync(decompressedRtf, { decode: iconvLite.decode });

      if (result.mode === 'html') {
        html = toText(result.text);
        text = html; // Use HTML as text fallback
      } else {
        // If not HTML mode, convert plain text to HTML
        text = toText(result.text);
        html = textToHtml(text); // Convert plain text to HTML format
      }

      return {
        text,
        html,
        attachments,
      };
    }

    throw new Error('Could not extract content from MSG file');
  }


  const readEmail = async () => {
    if (!file) {
      console.error('File is null. Cannot read email.');
      setIsLoading(false);
      return;
    }

    // Check if file is MSG format
    const isMsgFile = (file: File): boolean => {
      const extension = file.name?.toLowerCase().split('.').pop();
      return extension === 'msg' || file.type === 'application/vnd.ms-outlook';
    };

    let parsedContent: ParsedEmlContent;
    try {
      if (isMsgFile(file)) {
        const msgContent = await fetchParsedMsg(file);
        if (!msgContent) {
          throw new Error('Failed to parse MSG file');
        }
        parsedContent = msgContent;
      } else {
        parsedContent = await fetchParsedEml(file);
      }

      const images = extractImages(parsedContent);
      // TODO: image rendering not working, fix it
      const updatedHtml = replaceCidUrls(parsedContent.html, images);
      const sanitizedHtml = sanitizeHtml(updatedHtml);
      setEmailContent({ text: parsedContent.text, html: sanitizedHtml });
    } catch (error) {
      console.error('Error reading email:', error);
      setEmailContent({ text: '', html: '' });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    readEmail,
    isLoading,
    emailContent,
  };
};
